import {
  ArrowSquareOut,
  IdentificationCard,
  Lock,
  FileText,
  Quotes,
  TrendUp,
} from "@phosphor-icons/react";

const ACCENT = {
  saffron: {
    stripe: "bg-saffron",
    pill: "badge-saffron",
    soft: "bg-saffron/10",
    border: "border-saffron",
    accent: "text-saffron-hover",
  },
  indigo: {
    stripe: "bg-indigo",
    pill: "badge-indigo",
    soft: "bg-indigo/10",
    border: "border-indigo",
    accent: "text-indigo",
  },
  terracotta: {
    stripe: "bg-terracotta",
    pill: "badge-terracotta",
    soft: "bg-terracotta/10",
    border: "border-terracotta",
    accent: "text-terracotta",
  },
  teal: {
    stripe: "bg-teal",
    pill: "badge-teal",
    soft: "bg-teal/10",
    border: "border-teal",
    accent: "text-teal",
  },
};

function Stat({ label, value, accent }) {
  return (
    <div className="flex-1 min-w-[80px]" data-testid={`scopus-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline">{label}</div>
      <div className={`font-numeric font-light text-3xl lg:text-4xl mt-1 ${accent}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

export const ScopusProfiles = ({ profiles = [] }) => {
  if (!profiles.length) return null;
  return (
    <div data-testid="scopus-profiles" className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
        {profiles.map((p, i) => {
          const palette = ACCENT[p.accent] || ACCENT.saffron;
          return (
            <div
              key={p.scopus_id}
              className={`editorial-card p-6 lg:p-7 relative overflow-hidden ${palette.soft} avoid-break`}
              data-testid={`scopus-profile-${i}`}
            >
              <div className={`absolute top-0 left-0 h-1 w-full ${palette.stripe}`} />
              <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <IdentificationCard size={20} weight="duotone" className={palette.accent} />
                  <div className={`pill-color ${palette.pill}`}>{p.label}</div>
                </div>
                <div className="font-mono text-[11px] text-ink-mute uppercase tracking-wider">
                  Scopus ID · <span className="text-ink">{p.scopus_id}</span>
                </div>
              </div>

              <h3 className="font-display text-xl lg:text-2xl tracking-tight leading-tight">
                Chakraborty, Partha Pratim
              </h3>
              <div className="text-sm text-ink-mute italic mt-1.5">{p.affiliation}</div>

              {p.orcid && (
                <a
                  href={p.orcid_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 font-mono text-xs text-ink-mute hover:text-terracotta transition-colors"
                  data-testid={`scopus-orcid-${i}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal" />
                  ORCID · {p.orcid}
                  <ArrowSquareOut size={11} weight="bold" />
                </a>
              )}

              <div className="mt-6 pt-5 border-t border-paper-edge flex gap-5 flex-wrap">
                <Stat label="Documents" value={p.documents} accent={palette.accent} />
                <Stat label="Citations" value={p.citations} accent={palette.accent} />
                <Stat label="h-index" value={p.h_index} accent={palette.accent} />
                {p.co_authors > 0 && (
                  <Stat label="Co-authors" value={p.co_authors} accent={palette.accent} />
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-paper-edge flex flex-wrap gap-2 no-print">
                <a
                  href={p.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 border bg-white border-ink/15 text-ink hover:border-ink/40 text-[11px] font-mono uppercase tracking-wider"
                  data-testid={`scopus-public-link-${i}`}
                >
                  <ArrowSquareOut size={12} weight="bold" />
                  View on Scopus
                </a>
                <a
                  href={p.institutional_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 px-3.5 py-2 border bg-white border-teal text-teal hover:bg-teal/10 text-[11px] font-mono uppercase tracking-wider"
                  data-testid={`scopus-knimbus-link-${i}`}
                >
                  <Lock size={12} weight="bold" />
                  CIMR Knimbus (login)
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="font-mono text-[11px] text-ink-mute leading-relaxed max-w-3xl" data-testid="scopus-note">
        Detailed publication titles on Scopus require an authenticated session. Full lists are
        available through the CIMR Knimbus links above after institutional login.
      </div>
    </div>
  );
};

export default ScopusProfiles;
